/**
 * Every kind of object, as the eraser and the select tool need to see it.
 *
 * Those tools work on strokes — a list of points with a colour and a width — and they
 * were built before any object existed. Rather than teach them what a shape or a
 * sticky note is, each object lends them a stroke-shaped view of itself, and takes
 * back the changed view afterwards. Moving, resizing, turning, recolouring, erasing
 * and duplicating then work on everything on the board, with one code path.
 *
 * A shape lends its outline, because that is what a shape is (shape-object.ts). A
 * text box or a sticky note lends its four corners, traced round and then across both
 * diagonals — so the hand finds it anywhere on it, not only along its edge.
 */

import type { BoardObject, Point, Stroke } from '@opal/shared';

import { newObjectId } from '@opal/shared';

import { readString } from './object-data.js';

import {
  isShape,
  outlineBounds,
  outlinePointsOf,
  shapeFromStroke,
  shapeStroke,
} from './shape-object.js';
import {
  fontSizeOf,
  isTextual,
  MAX_FONT_SIZE,
  MIN_FONT_SIZE,
  withData,
} from './text-object.js';

/** A box has no line of its own, but a stroke must have a width to be measured by. */
const BOX_LINE_WIDTH = 1;

/** What a box's stroke view is coloured when it says nothing about its own colour. */
const DEFAULT_INK = '#1a1a2e';

const boxViews = new WeakMap<BoardObject, Stroke>();

function turn(point: Point, about: Point, angle: number): Point {
  if (angle === 0) return point;
  const sin = Math.sin(angle);
  const cos = Math.cos(angle);
  const dx = point.x - about.x;
  const dy = point.y - about.y;
  return { x: about.x + dx * cos - dy * sin, y: about.y + dx * sin + dy * cos };
}

/** The four corners of an object's box, in the order top-left, then clockwise. */
export function boxCorners(object: BoardObject): Point[] {
  const right = object.x + object.width;
  const bottom = object.y + object.height;
  const centre = { x: object.x + object.width / 2, y: object.y + object.height / 2 };
  return [
    { x: object.x, y: object.y },
    { x: right, y: object.y },
    { x: right, y: bottom },
    { x: object.x, y: bottom },
  ].map((corner) => turn(corner, centre, object.rotation));
}

/**
 * The path the hand can find a box by: round the edge, then across both diagonals.
 *
 * The diagonals are what make the middle of a sticky note clickable. Nothing draws
 * this path — the note is painted as paper and words — so they cost nothing to look at.
 */
export function boxOutline(object: BoardObject): Point[] {
  const [topLeft, topRight, bottomRight, bottomLeft] = boxCorners(object);
  if (!topLeft || !topRight || !bottomRight || !bottomLeft) return [];
  return [topLeft, topRight, bottomRight, bottomLeft, topLeft, bottomRight, topRight, bottomLeft];
}

/** True when a canvas point lies inside an object's box, turning included. */
export function boxContains(object: BoardObject, point: Point): boolean {
  const centre = { x: object.x + object.width / 2, y: object.y + object.height / 2 };
  const upright = turn(point, centre, -object.rotation);
  return (
    upright.x >= object.x &&
    upright.x <= object.x + object.width &&
    upright.y >= object.y &&
    upright.y <= object.y + object.height
  );
}

/** The points an object lends the tools that work on it. */
export function objectOutline(object: BoardObject): readonly Point[] {
  return isShape(object) ? outlinePointsOf(object) : boxOutline(object);
}

function flatten(points: readonly Point[]): Float32Array {
  const flat = new Float32Array(points.length * 3);
  points.forEach((point, index) => {
    flat[index * 3] = point.x;
    flat[index * 3 + 1] = point.y;
    // A placed thing has an even line: no pressure, no thick and thin.
    flat[index * 3 + 2] = 1;
  });
  return flat;
}

/** A box object as a stroke. Kept for as long as the object lives, like a shape's. */
function boxStroke(object: BoardObject): Stroke {
  const cached = boxViews.get(object);
  if (cached) return cached;

  const view: Stroke = {
    id: object.id,
    userId: object.userId,
    tool: 'pen',
    points: flatten(boxOutline(object)),
    canvasWidth: BOX_LINE_WIDTH,
    // The colour the select tool would recolour: the words, for a text box.
    color: isTextual(object) ? readString(object.data, 'color', DEFAULT_INK) : DEFAULT_INK,
    opacity: 1,
    layer: object.layer,
    createdAt: object.createdAt,
  };
  boxViews.set(object, view);
  return view;
}

/** Any object, as the stroke the tools work on. */
export function objectStroke(object: BoardObject): Stroke {
  return isShape(object) ? shapeStroke(object) : boxStroke(object);
}

/** The canvas-space box an object covers, whatever kind it is. */
export function objectArea(object: BoardObject): {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
} {
  return isShape(object)
    ? outlineBounds(outlinePointsOf(object))
    : { x: object.x, y: object.y, width: object.width, height: object.height };
}

function pointAt(stroke: Stroke, index: number): Point {
  return { x: stroke.points[index * 3] ?? 0, y: stroke.points[index * 3 + 1] ?? 0 };
}

/**
 * Where a box ended up after a drag: moved, resized or turned.
 *
 * Read off the corners rather than off a bounding box, so a note turned 30° is a note
 * turned 30° and not a bigger upright one. The words grow and shrink with the box for
 * the same reason ink does (selection.ts): writing scaled to twice the size is
 * written twice as large.
 */
function boxFromStroke(object: BoardObject, stroke: Stroke): BoardObject {
  const topLeft = pointAt(stroke, 0);
  const topRight = pointAt(stroke, 1);
  const bottomRight = pointAt(stroke, 2);
  const bottomLeft = pointAt(stroke, 3);

  const width = Math.hypot(topRight.x - topLeft.x, topRight.y - topLeft.y);
  const height = Math.hypot(bottomLeft.x - topLeft.x, bottomLeft.y - topLeft.y);
  const rotation = Math.atan2(topRight.y - topLeft.y, topRight.x - topLeft.x);
  const centre = { x: (topLeft.x + bottomRight.x) / 2, y: (topLeft.y + bottomRight.y) / 2 };

  const grew = object.width > 0 ? width / object.width : 1;
  const size = Math.min(Math.max(fontSizeOf(object) * grew, MIN_FONT_SIZE), MAX_FONT_SIZE);
  const resized = Math.abs(grew - 1) > 0.001 ? withData(object, { fontSize: size }) : object;

  return {
    ...resized,
    x: centre.x - width / 2,
    y: centre.y - height / 2,
    width,
    height,
    rotation,
    // A text box is recoloured by recolouring its words. A sticky's paper is chosen
    // from its own swatches, so the select tool leaves it alone.
    data:
      object.kind === 'text'
        ? { ...resized.data, color: stroke.color }
        : resized.data,
    updatedAt: Date.now(),
  };
}

/** The object a changed stroke view describes, whatever kind it is. */
export function objectFromStroke(object: BoardObject, stroke: Stroke): BoardObject {
  return isShape(object) ? shapeFromStroke(object, stroke) : boxFromStroke(object, stroke);
}

/**
 * True when the eraser may cut this into pieces.
 *
 * A shape is a line, and half a line is still a line. Half a sticky note is nothing
 * at all, so the eraser takes those whole however it is set (SRS C4).
 */
export function isCuttable(object: BoardObject): boolean {
  return isShape(object);
}

/** A second copy of an object, offset so it is visibly a copy. */
export function duplicateObject(object: BoardObject, offset: number): BoardObject {
  const now = Date.now();
  const moved = isShape(object)
    ? {
        ...object,
        data: {
          ...object.data,
          points: outlinePointsOf(object).map((point) => ({
            x: point.x + offset,
            y: point.y + offset,
          })),
        },
      }
    : object;

  return {
    ...moved,
    id: newObjectId(),
    x: object.x + offset,
    y: object.y + offset,
    zIndex: now,
    createdAt: now,
    updatedAt: now,
  };
}
